import { useState } from "react";
import { GetProducts } from "./queries";
import { CreateVideo } from "./mutations";
import { useQuery } from "@apollo/react-hooks";
import { useMutation } from "@apollo/react-hooks";
import UploadHelper from "../../helpers/upload";
import useTutorialsState from "../../hooks/useTutorialsState";
import useTagAccount from "../../hooks/useTagAccount";
import { useMixpanel } from "react-mixpanel-browser";
import { GetSubscriptionLimit } from "../SubscriptionLimit/queries";

export function useFileUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0.0);
  const [uploadedKey, setUploadedKey] = useState();

  function handleProgressEvent({ loaded, total }) {
    setProgress(loaded / total);
  }

  async function upload(file) {
    const videoFile = file[0];

    const { signedUrl, key } = await UploadHelper.getSignedUrl(
      UploadHelper.VIDEO
    );

    setUploading(true);
    setUploadedKey(key);

    await UploadHelper.uploadToS3(signedUrl, videoFile, handleProgressEvent);

    setProgress(1);
  }

  function resetUpload() {
    setUploading(false);
    setProgress(0.0);
    setUploadedKey();
  }

  return { uploading, progress, uploadedKey, upload, resetUpload };
}

export function useCreateVideo({ onDismiss, refetchVideos }) {
  const [loading, setLoading] = useState(false);
  const [createVideoMutation] = useMutation(CreateVideo, {
    refetchQueries: [
      {
        query: GetSubscriptionLimit
      }
    ]
  });

  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();
  const { tagAccount } = useTagAccount();
  const mixpanel = useMixpanel();

  async function createVideo(videoName, productId, wowzaId) {
    setLoading(true);

    const {
      data: {
        createVideo: { id: videoId }
      }
    } = await createVideoMutation({
      variables: {
        video: {
          name: videoName,
          product: productId,
          wowzaId,
          live: false,
          scheduleStatus: "Disabled",
          type: "Upload"
        }
      }
    });

    const hasAddedVideo = getHasCompletedTutorial("Add Video");

    if (!hasAddedVideo) {
      await completeTutorial({
        variables: { name: "Add Video", version: 1 }
      });

      await tagAccount("Video Created");
    }

    mixpanel.track("Video Created", {
      "Video ID": videoId,
      "Product ID": productId,
      "Video Type": "Upload"
    });

    await refetchVideos();

    setLoading(false);
    onDismiss();
  }

  return { createVideo, loading };
}

export function useScheduleBroadcast({ onDismiss, refetchVideos }) {
  const [loading, setLoading] = useState(false);
  const [createVideoMutation] = useMutation(CreateVideo, {
    refetchQueries: [
      {
        query: GetSubscriptionLimit
      }
    ]
  });

  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();
  const { tagAccount } = useTagAccount();
  const mixpanel = useMixpanel();

  async function createVideo(name, scheduleStartTime, product) {
    setLoading(true);

    const {
      data: {
        createVideo: { id: videoId }
      }
    } = await createVideoMutation({
      variables: {
        video: {
          name,
          scheduleStartTime,
          scheduleStatus: "Enabled",
          product,
          live: false,
          type: "Upload"
        }
      }
    });

    const hasAddedVideo = getHasCompletedTutorial("Add Schedule Broadcast");

    if (!hasAddedVideo) {
      await completeTutorial({
        variables: { name: "Add Schedule Broadcast", version: 1 }
      });
    }

    await refetchVideos();

    setLoading(false);
    onDismiss();
  }

  return { createVideo, loading };
}

export function useProductList() {
  const { data, loading } = useQuery(GetProducts);
  const ownedProducts =
    (!loading && data && data.stores && data.stores.ownedProducts) || [];
  const partneredProducts =
    (!loading &&
      data &&
      data.accounts &&
      data.accounts[0].partneredProducts.map((elem) => ({
        ...elem,
        partneredProduct: true
      }))) ||
    [];
  const products = [...ownedProducts, ...partneredProducts];
  return {
    products,
    loading
  };
}
